import { useCallback } from "react";
import {
  GetAccount200ResponsePositionsInner,
  OrderTypeResponse,
  SideResponse,
} from "../../codegen-api";
import { ICreateOrderBody } from "../../interfaces/Order";
import { selectTpslTriggers } from "../../hooks/tpsl/tpsl.selectors";
import { TpslDesiredState } from "../../hooks/tpsl/tpsl.types";
import { useTpslForPosition } from "../../hooks/tpsl/useTpslForPosition";
import type { useGetAccount } from "../../hooks/api/account/useGetAccount";
import type { useOrderActions } from "./useOrderActions";

interface UseTPSLActionsParams {
  createOrder: ReturnType<typeof useOrderActions>["createOrder"];
  mutateAccount: ReturnType<typeof useGetAccount>["mutate"];
}

/**
 * Position TP/SL no longer rides the Aevo `/orders` endpoint — the perp backend
 * rejects stop/trigger orders there. Triggers now live in the off-chain TP/SL
 * executor, reached through the backend's `/tpsl/*` proxy with EIP-712 auth
 * (see src/hooks/tpsl). `closePosition` is unrelated and still uses `/orders`.
 */
export function useTPSLActions({
  createOrder,
  mutateAccount,
}: UseTPSLActionsParams) {
  const {
    supported: tpslSupported,
    openOrders: tpslOpenOrders,
    submitTpslForPosition,
  } = useTpslForPosition();

  /** Live TP/SL for a position, in the Aevo `{ order_id, trigger }` shape. */
  const tpslTriggersForPosition = useCallback(
    (position: GetAccount200ResponsePositionsInner | undefined) =>
      selectTpslTriggers(tpslOpenOrders, position),
    [tpslOpenOrders]
  );

  // To close position, submit a market order matching the same exact position size
  // This also mutates the useGetAccount()
  const closePosition = useCallback(
    async (position: any) => {
      const data: ICreateOrderBody = {
        amount: position.amount,
        side:
          position.side === SideResponse.Buy
            ? SideResponse.Sell
            : SideResponse.Buy,
        instrument: position.instrument_id,
        orderType: OrderTypeResponse.Market,
        reduceOnly: true,
        stop: undefined,
        trigger: undefined,
      };
      // No need to try catch because createOrder already handled error
      const res = await createOrder(data);
      if (res.order_id) {
        mutateAccount();
      }
      return res;
    },
    [createOrder, mutateAccount]
  );

  /**
   * Reconcile a position's triggers with the modal's desired end state.
   * Create (one POST, 1-2 legs), edit (cancel old + create new) and remove
   * (DELETE only) all go through this single call.
   */
  const createTPSLOrders = useCallback(
    async (
      position: GetAccount200ResponsePositionsInner | undefined,
      desired: TpslDesiredState
    ) => {
      const rows = await submitTpslForPosition(position, desired);
      mutateAccount();
      return rows;
    },
    [mutateAccount, submitTpslForPosition]
  );

  return {
    closePosition,
    createTPSLOrders,
    tpslSupported,
    tpslTriggersForPosition,
  };
}
